import {
	AlertCircle,
	AlertTriangle,
	CheckCircle,
	InfoCircle,
	X,
} from "@untitledui/icons";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Toaster as Sonner, type ToasterProps, toast } from "sonner";
import { useTheme } from "@/providers/theme";
import { cx } from "@/utils/cx";
import { Spinner } from "../spinner";

/** Above modals/drawers and React Aria overlay stacks. */
const TOASTER_Z_INDEX = 99_999;

export const Toaster = ({ className, style, ...props }: ToasterProps) => {
	const { theme } = useTheme();
	const [isMounted, setIsMounted] = useState(false);

	useEffect(() => {
		setIsMounted(true);
	}, []);

	const sonner = (
		<Sonner
			className={cx("toaster group", className)}
			duration={5000}
			icons={{
				success: (
					<span className="flex shrink-0 items-center">
						<CheckCircle className="size-4" />
					</span>
				),
				info: (
					<span className="flex shrink-0 items-center">
						<InfoCircle className="size-4" />
					</span>
				),
				warning: (
					<span className="flex shrink-0 items-center">
						<AlertCircle className="size-4" />
					</span>
				),
				error: (
					<span className="flex shrink-0 items-center">
						<AlertCircle className="size-4" />
					</span>
				),
				loading: (
					<span className="flex shrink-0 items-center">
						<Spinner className="size-4" />
					</span>
				),
			}}
			position="top-center"
			richColors
			style={
				{
					zIndex: TOASTER_Z_INDEX,
					"--normal-bg": "var(--popover)",
					"--normal-text": "var(--popover-foreground)",
					"--normal-border": "var(--border)",
					"--border-radius": "var(--radius)",
					...style,
				} as React.CSSProperties
			}
			theme={(theme ?? "light") as ToasterProps["theme"]}
			toastOptions={{
				unstyled: true,
				classNames: {
					toast:
						"pointer-events-auto flex items-center gap-3 rounded-[8px] p-2 w-full",
					content: "w-full",
				},
			}}
			{...props}
		/>
	);

	if (!isMounted) {
		return null;
	}

	// React Aria marks everything outside modals as inert; top-layer keeps toasts interactive.
	return createPortal(
		<div data-react-aria-top-layer="true" data-slot="toaster-portal">
			{sonner}
		</div>,
		document.body
	);
};

type ToastCustomProps = {
	title?: string;
	description?: string;
	variant?: "success" | "error" | "warning";
};
export const ToastCustom = ({
	title,
	description,
	variant = "error",
}: ToastCustomProps) => (
	<div className="flex w-full min-w-full! flex-col gap-2 rounded-xl border border-border-primary bg-bg-primary_alt shadow-xs">
		<div className="flex w-full justify-between px-2 pt-2">
			<div className="relative size-[42.5px]">
				<div
					className={cx(
						"-translate-[3.5px] absolute size-[42.5px] rounded-3xl border-2 border-fg-error-primary opacity-10",
						variant === "success" && "border-fg-success-primary",
						variant === "warning" && "border-fg-warning-primary"
					)}
				/>
				<div
					className={cx(
						"translate-[3px] absolute size-[30px] rounded-3xl border-2 border-fg-error-primary opacity-30",
						variant === "success" && "border-fg-success-primary",
						variant === "warning" && "border-fg-warning-primary"
					)}
				/>
				{variant === "success" && (
					<CheckCircle className="translate-2 absolute size-5 text-fg-success-primary" />
				)}
				{variant === "warning" && (
					<AlertTriangle className="translate-2 absolute size-5 text-fg-warning-primary" />
				)}
				{variant === "error" && (
					<AlertCircle className="translate-2 absolute size-5 text-fg-error-primary" />
				)}
			</div>
			<button
				aria-label="Fechar notificação"
				className="p-2"
				onClick={() => toast.dismiss()}
				type="button"
			>
				<X className="size-5 cursor-pointer text-fg-quaternary" />
			</button>
		</div>

		<div className="flex flex-col gap-1 px-4 pb-4">
			<h5 className="font-body font-semibold text-secondary text-sm leading-5">
				{title}
			</h5>
			<p className="font-body font-normal text-sm text-tertiary leading-5">
				{description}
			</p>
		</div>
	</div>
);
